<template>
  <div>
    <v-container>
      <div class="back-link" @click="goBackToTierSelection">
        <img src="@/assets/images/icons/arrow-left.svg" alt="Back" class="back-icon" />
        <span>Return to Tier Selection</span>
      </div>
              <div class="header-section">
            <h1>Enter Your Billing Address</h1>
        </div>
      <v-card>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="billingInfo.first_name"
                  label="First Name"
                  :rules="[rules.required]"
                  required
                  variant="outlined"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="billingInfo.last_name"
                  label="Last Name"
                  :rules="[rules.required]"
                  required
                  variant="outlined"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="billingInfo.organization"
                  label="Organization (Optional)"
                  variant="outlined"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="billingInfo.billing_email"
                  label="Billing Email (Optional)"
                  variant="outlined"
                  :rules="[rules.email]"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="billingInfo.vat_number"
                  label="VAT Number (Optional)"
                  variant="outlined"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="billingInfo.address_line_1"
                  label="Address Line 1"
                  :rules="[rules.required]"
                  required
                  variant="outlined"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="billingInfo.address_line_2"
                  label="Address Line 2 (Optional)"
                  variant="outlined"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="billingInfo.city"
                  label="City"
                  :rules="[rules.required]"
                  required
                  variant="outlined"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="billingInfo.country"
                  :items="countries"
                  item-title="name"
                  item-value="isoCode"
                  label="Country"
                  :rules="[rules.required]"
                  required
                  variant="outlined"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="billingInfo.state"
                  :items="states"
                  item-title="name"
                  item-value="isoCode"
                  label="State"
                  :rules="[rules.required]"
                  required
                  variant="outlined"
                  :disabled="!billingInfo.country"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="billingInfo.zip_code"
                  label="Zip/Postal Code"
                  :rules="[rules.required]"
                  required
                  variant="outlined"
                ></v-text-field>
              </v-col>
            </v-row>
            <div v-if="errorMessage" class="error-message mt-4 text-red-600 font-semibold border border-red-200 p-3 rounded bg-red-50">
                Error: {{ errorMessage }}
            </div>
            <v-btn :disabled="!valid || isLoading" color="primary" class="mt-4 str-btn-primary medium" @click="submitBillingInfo">
                <v-progress-circular v-if="isLoading" indeterminate size="24"></v-progress-circular>
                <span v-else>Submit</span>
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAuth0 } from '@auth0/auth0-vue';
import { Country, State } from 'country-state-city';
import axios from 'axios';

const store = useStore();
const router = useRouter();
const { getAccessTokenSilently, user } = useAuth0();

const valid = ref(false);
const form = ref(null);
const errorMessage = ref(null);
const isLoading = ref(false);

const countries = ref(Country.getAllCountries());
const states = ref([]);

const billingInfo = ref({
  first_name: '',
  last_name: '',
  organization: '',
  vat_number: '',
  billing_email: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  country: '',
  zip_code: '',
});

onMounted(() => {
  // Load saved data from Vuex
  const savedBillingInfo = store.state.billingInfoForm;
  Object.assign(billingInfo.value, savedBillingInfo);

  // If a country was previously selected, load its states
  if (billingInfo.value.country) {
    states.value = State.getStatesOfCountry(billingInfo.value.country);
  }
});

// Watch for changes in billingInfo ref and commit to Vuex
watch(billingInfo.value, (newVal) => {
  store.commit('SET_BILLING_INFO_FORM', newVal);
});

// Watch for country change to update states dropdown
watch(() => billingInfo.value.country, (newCountryCode, oldCountryCode) => {
  if (newCountryCode) {
    states.value = State.getStatesOfCountry(newCountryCode);

    // Check if the previously selected state is valid for the new country
    const currentStateIsoCode = billingInfo.value.state;
    const stateExistsInNewCountry = states.value.some(s => s.isoCode === currentStateIsoCode);

    if (newCountryCode !== oldCountryCode && !stateExistsInNewCountry) {
      billingInfo.value.state = ''; // Clear only if country changed and state is not valid
    }
  } else {
    states.value = [];
    billingInfo.value.state = ''; // Always clear state if country is cleared
  }
});

const rules = {
  required: value => !!value || 'Required.',
  email: value => {
    if (!value) return true; // Optional field
    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(value) || 'Invalid e-mail.';
  },
};

const goBackToTierSelection = () => {
  store.commit('CLEAR_BILLING_INFO_FORM'); // Clear billing info from store
  store.commit('CLEAR_SELECTED_TIER');
  router.push({ name: 'SubscriptionTierSelection' });
};

const submitBillingInfo = async () => {
  if (!form.value.validate()) {
    return;
  }

  isLoading.value = true;
  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };

    await axios.post(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/billing/address/${user.value.sub}`, {
        ...billingInfo.value,
        auth0_id: user.value.sub,
    }, { headers });

    const authData = store.state.authentication;
    store.commit('SET_AUTHENTICATION', {
        ...authData,
        hasBillingInfo: true,
    });

    if (store.state.authentication.tier) {
        router.push({ name: 'GetStarted' });
    } else {
        router.push({ name: 'CheckoutView' });
    }
  } catch (error) {
    console.error('Failed to submit billing info:', error);
    errorMessage.value = error.response?.data?.detail || 'An unexpected error occurred.';
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped lang="scss">
    @import '@/assets/styles/StrButtons.scss'
    @import '@/assets/styles/BillingInfo.scss'
</style>
